import { useEffect, useRef } from 'react'
import compact from 'lodash.compact'
import { useAppDispatch, useAppSelector } from 'state/store'
import { interactionModelOperations } from '../../redux'
import { Activity } from 'types/shared'
import { resolveActivity } from './resolvers'

type Props = {
  draftInteractionUuid: string
}

export default function useApplyDefaultValues({ draftInteractionUuid }: Props) {
  const dispatch = useAppDispatch()
  const hasApplied = useRef(false)

  const interactionModel = useAppSelector((state) => state.interactionModel)
  const interactionModelData = interactionModel.flatData

  const hasPendingDraft =
    !!draftInteractionUuid && !interactionModel.isDraftApplied
  const hasPendingBaseInteraction =
    !draftInteractionUuid &&
    !!interactionModelData?.base_interaction &&
    !interactionModel.isBaseInteractionApplied

  const isReady =
    !!interactionModelData && !hasPendingDraft && !hasPendingBaseInteraction

  useEffect(() => {
    if (!isReady || hasApplied.current || !interactionModelData) return

    hasApplied.current = true

    const activityModelUuids = interactionModelData.steps.flatMap(
      (step) => step.activity_models
    )

    const activityModels = compact(
      activityModelUuids.map(
        (uuid) => interactionModelData.activity_models[uuid]
      )
    )

    const promises = compact(
      activityModels.map((activityModel) =>
        resolveActivity(activityModel, interactionModelData.activity_values)
      )
    ) as Promise<Activity>[]

    Promise.allSettled(promises).then((results) => {
      const defaultActivities = compact(
        results.map((result) =>
          result.status === 'fulfilled' ? result.value : null
        )
      ) as Activity[]

      if (defaultActivities.length) {
        dispatch(interactionModelOperations.updateActivities(defaultActivities))
      }
    })
  }, [isReady, interactionModelData, dispatch])
}
